import {
  AppAlertStrip,
  type AlertStripTone,
} from "@/components/ui/AppAlertStrip";
import { useUserSubscriptionState } from "@/hooks/useUserSubscriptionState";
import { URLS } from "@/lib/constants";
import {
  dismissSubscriptionAlertForToday,
  isSubscriptionAlertSuppressed,
  snoozeSubscriptionAlert,
} from "@/lib/subscriptionAlertDismiss";
import { deriveSubscriptionUi } from "@/lib/subscriptionUi";
import { useAuth } from "@/lib/AuthContext";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { YStack } from "tamagui";

function formatDate(
  iso: string | null,
  opts?: Intl.DateTimeFormatOptions,
): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // Billing / card dates are UTC midnight/end-of-day — format in UTC so
  // e.g. 2026-07-31T23:59:59Z stays “Jul 2026”, not “Aug” in UTC+2.
  return d.toLocaleDateString(undefined, {
    timeZone: "UTC",
    ...(opts ?? {
      month: "long",
      day: "numeric",
      year: "numeric",
    }),
  });
}

/** WP-style “Jul 2026” for card expiry (UTC month/year). */
function formatMonthYear(iso: string | null): string {
  return formatDate(iso, { month: "short", year: "numeric", timeZone: "UTC" });
}

function buildExpiringMessage(
  cardExpiresAt: string | null,
  nextPaymentAt: string | null,
): string {
  const cardExpires = formatMonthYear(cardExpiresAt);
  const renewal = formatDate(nextPaymentAt);

  if (cardExpires && renewal) {
    return `Your credit card expires at the end of ${cardExpires}. Your next renewal is ${renewal}. Please update it to avoid losing access.`;
  }
  if (cardExpires) {
    return `Your credit card expires at the end of ${cardExpires}. Please update it to avoid losing access.`;
  }
  if (renewal) {
    return `Your credit card is expiring soon. Your next renewal is ${renewal}. Please update it to avoid losing access.`;
  }
  return "Your credit card is expiring soon. Please update it to avoid losing access.";
}

type AlertItem = {
  key: string;
  tone: AlertStripTone;
  message: string;
  actionLabel: string;
  href: string;
  /** Card payment alerts get Remind me later + X (like WP). */
  dismissable: boolean;
};

/**
 * Under-header alerts: card expiring/expired/declined + cancel countdown.
 *
 * Remind me later → snooze 3 days (local).
 * X → hide for the rest of today (local).
 */
export function SubscriptionHeaderAlerts() {
  const { user } = useAuth();
  const userId = user?.id;
  const { data: row = null } = useUserSubscriptionState({ userId });
  const ui = useMemo(() => deriveSubscriptionUi(row), [row]);
  const [hiddenKeys, setHiddenKeys] = useState<Record<string, boolean>>({});

  const paymentHref = ui.paymentUpdateUrl?.trim() || URLS.MY_ACCOUNT;
  const cards: AlertItem[] = [];

  if (ui.cardMessage === "declined") {
    cards.push({
      key: "declined",
      tone: "danger",
      message:
        "Your last payment didn’t go through. Update your card to keep access.",
      actionLabel: "Update Payment Method",
      href: paymentHref,
      dismissable: true,
    });
  } else if (ui.cardMessage === "expiring") {
    cards.push({
      key: "expiring",
      tone: "warning",
      message: buildExpiringMessage(ui.cardExpiresAt, ui.nextPaymentAt),
      actionLabel: "Update Payment Method",
      href: paymentHref,
      dismissable: true,
    });
  } else if (ui.cardMessage === "expired") {
    cards.push({
      key: "expired",
      tone: "danger",
      message:
        "Your card has expired. Update your payment method to stay subscribed.",
      actionLabel: "Update Payment Method",
      href: paymentHref,
      dismissable: true,
    });
  }

  if (ui.showCancelCountdown) {
    const when = formatDate(ui.accessEndsAt, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    cards.push({
      key: "pending-cancel",
      tone: "info",
      message: when
        ? `Your subscription ends on ${when}. Reactivate anytime to keep access.`
        : "Your subscription is ending soon. Reactivate anytime to keep access.",
      actionLabel: "Reactivate",
      href: URLS.MY_ACCOUNT,
      dismissable: true,
    });
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userId || !cards.length) {
        if (!cancelled) setHiddenKeys({});
        return;
      }
      const next: Record<string, boolean> = {};
      await Promise.all(
        cards.map(async (c) => {
          next[c.key] = await isSubscriptionAlertSuppressed(userId, c.key);
        }),
      );
      if (!cancelled) setHiddenKeys(next);
    })();
    return () => {
      cancelled = true;
    };
    // Re-check when which alerts are eligible changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, ui.cardMessage, ui.showCancelCountdown, ui.cardExpiresAt, ui.accessEndsAt]);

  const hideLocally = useCallback((key: string) => {
    setHiddenKeys((prev) => ({ ...prev, [key]: true }));
  }, []);

  const onRemindLater = useCallback(
    (key: string) => {
      void snoozeSubscriptionAlert(userId, key, 3);
      hideLocally(key);
    },
    [hideLocally, userId],
  );

  const onDismiss = useCallback(
    (key: string) => {
      void dismissSubscriptionAlertForToday(userId, key);
      hideLocally(key);
    },
    [hideLocally, userId],
  );

  const visible = cards.filter((c) => !hiddenKeys[c.key]);
  if (!visible.length) return null;

  return (
    <YStack w="100%">
      {visible.map((c) => (
        <AppAlertStrip
          key={c.key}
          tone={c.tone}
          message={c.message}
          href={c.href}
          actionLabel={c.actionLabel}
          onRemindLater={
            c.dismissable ? () => onRemindLater(c.key) : undefined
          }
          onDismiss={c.dismissable ? () => onDismiss(c.key) : undefined}
        />
      ))}
    </YStack>
  );
}
